//================================================================
//  Tab: In Progress
//================================================================

//  Purpose: This tab displays all the requests that are current in progress

//  Properties:
//    - requests = {An array (useState) - this is the list of all the Procurement requests that are in progress}
//    - setRequests = {useState, used to set the requests state}

//  Example:
//    <InProgress
//      requests={requests}
//      setRequests={setRequests}
//    ></InProgress>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

//Contexts
import { GetUser } from '../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import ExportToCSV from '../../../Components/ExportToCSV/ExportToCSV';
import AssignTask from '../../../Components/AssignTask/AssignTask';
import StatusLabel from '../../../Components/StatusLabel/StatusLabel';

//Functions
import QueryListener from '../../../Library/QueryListener';
import ConvertDate from '../../../Library/ConvertDate';

//Images
import IconSearch from '../../../Components/Images/Icon_Search_Grey.svg';

export default function InProgress({
  requests,
  setRequests
}) {
  //------------------------------------------------------
  //  useContext and React Router
  //------------------------------------------------------

  const getUser = useContext(GetUser);
  const navigate = useNavigate();

  //------------------------------------------------------
  //  useStates
  //------------------------------------------------------

  // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
  const [pageStatus, setPageStatus] = useState();

  // Search Input
  const [searchInput, setSearchInput] = useState('');

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Handles search function
  function handleSearch(value) {

    setSearchInput(value);

    // Reset the Filter
    if (value.length === 0) {
      setRequests({ 'inProgressRequestsFiltered': requests.inProgressRequests });

    };

    // Search Filter
    //  1. Request ID
    //  2. Short Description
    //  3. Assigned To
    const searchResults = requests.inProgressRequests.filter((object) =>
      object?.parentrequestid?.toLowerCase().includes(value.toLowerCase()) ||
      object?.shortdescription?.toLowerCase().includes(value.toLowerCase()) ||
      object?.assignedto?.email?.toLowerCase().includes(value.toLowerCase())
    );

    // Save the filtered search results to the useState
    setRequests({ 'inProgressRequestsFiltered': searchResults });

  };

  //------------------------------------------------------
  //  useEffects
  //------------------------------------------------------

  // Query Listener
  // - Get all Procurement requests that are in progress
  useEffect(() => {

    function onLoadChange(documents) {

      // Sort by Created Date (Newest to Oldest)
      documents.sort(function (a, b) {
        return b.createddate - a.createddate;
      });

      setRequests({ 'inProgressRequests': documents });
      setRequests({ 'inProgressRequestsFiltered': documents });
    }

    function onError(error) {
      console.log(error);
      setPageStatus('error-fatal');
    }

    const unsubscribe = QueryListener('requests', [
      ['tasktype', '==', 'Procurement'],
      ['assignmentgroup', '==', 'Procurement'],
      ['status', '==', 'In Progress'],
    ], onLoadChange, onLoadChange, onError);

    return () => {
      unsubscribe();
    };

    // eslint-disable-next-line
  }, [getUser])

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  return (
    <PageComponent
      status={pageStatus}
      body={
        <div className='flex flex-col px-[15px] gap-[20px]'>

          {/* ----------------------------------- */}
          {/*  Export                             */}
          {/* ----------------------------------- */}

          <div className='flex flex-row justify-end items-center gap-3'>

            {/* Download Report */}
            <ExportToCSV
              filename={`InProgress-Requests-${Date.now()}`}
              data={requests?.inProgressRequestsFiltered}
            ></ExportToCSV>

          </div>

          {/* ----------------------------------- */}
          {/*  Requests Table                     */}
          {/* ----------------------------------- */}

          <div className='Table-Container'>

            {/* ======== Sorting and Filters ========== */}
            <div className='flex flex-row justify-between py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

              <label className='font-semibold self-center'>
                Total: {requests?.inProgressRequestsFiltered.length} of {requests?.inProgressRequests.length}
              </label>

              {/* Search Bar */}
              <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[300px]'>

                <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                <label htmlFor='searchInput'>
                  <input
                    id='searchInput'
                    className='border-none h-[40px] p-0 m-0 outline-none'
                    type='text'
                    placeholder='Search'
                    onChange={(e) => handleSearch(e.target.value)}
                    value={searchInput}
                  ></input>
                </label>
              </div>

            </div>

            <div className='overflow-y-auto h-[calc(100vh-360px)]'>
              <table className='min-w-full max-h-96 table-auto'>
                <thead>
                  <tr className='sticky top-0 bg-white shadow-md z-10'>
                    <th className='py-[15px] px-[20px] font-medium'>Request ID</th>
                    <th className='py-[15px] px-[20px] font-medium'>Customer Request ID</th>
                    <th className='py-[15px] px-[20px] font-medium'>Created</th>
                    <th className='py-[15px] px-[20px] font-medium'>Short Description</th>
                    <th className='py-[15px] px-[20px] font-medium'>Vendor</th>
                    <th className='py-[15px] px-[20px] font-medium'>Status</th>
                    <th className='py-[15px] px-[20px] font-medium'>Last Modified</th>
                    <th className='py-[15px] px-[20px] font-medium'>Assigned To</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    // No requests found
                    requests?.inProgressRequestsFiltered?.length === 0 ?
                      (
                        <tr>
                          <td colSpan={7}>
                            <div className='text-center p-[2%]'>
                              No requests found.
                            </div>
                          </td>
                        </tr>
                      )
                      :
                      (
                        // Map in each Procurement requests
                        requests?.inProgressRequestsFiltered?.map((request, index) => (
                          <tr key={index} className='border-b-[#C8C8C9] border-solid border-b hover:bg-[#F0F7F7] '>

                            {/* Request ID */}
                            <td
                              className='py-[15px] px-[20px] font-medium uppercase cursor-pointer hover:underline'
                              onClick={() => navigate(`/requests/${request?.requestid}`)}
                            >
                              {request?.requestid}
                            </td>

                            {/* Customer Request ID */}
                            <td className='py-[15px] px-[20px] font-medium uppercase'>
                              {request?.parentrequestid}
                            </td>

                            {/* Created Date */}
                            <td className='py-[15px] px-[20px]'>{ConvertDate(request?.createddate)}</td>

                            {/* Short Description */}
                            <td className='py-[15px] px-[20px]'>
                              <div className='max-w-[250px] leading-normal whitespace-nowrap overflow-hidden text-ellipsis'>
                                {request?.taskdescription}
                              </div>
                            </td>

                            {/* Vendor  */}
                            <td className='py-[15px] px-[20px]'>{request?.vendor?.vendorname}</td>

                            {/* Status  */}
                            <td className='py-[15px] px-[20px]'>
                              <StatusLabel status={request?.status}></StatusLabel>
                            </td>

                            {/* Last Modified	 */}
                            <td className='py-[15px] px-[20px]'>{ConvertDate(request?.lastmodifieddate)}</td>

                            {/* Assigned To */}
                            <td className='py-[15px] px-[10px]'>
                              <AssignTask
                                query={['roles.procurementUser', '==', true]}
                                currentRequest={request}
                                lastItem={(requests?.inProgressRequestsFiltered?.length > 5 && (index + 1) > (requests?.inProgressRequestsFiltered?.length - 3))}
                              ></AssignTask>
                            </td>

                          </tr>
                        ))
                      )
                  }
                </tbody>
              </table>
            </div>
            
          </div>

        </div>
      }
    ></PageComponent>
  )
}